import { useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Loader2, Copy, ExternalLink, Smartphone, Banknote, Landmark, ScrollText, Check } from 'lucide-react';
import { api, buildUpiIntentUrl, fmtMoney } from '../lib/api';

const METHODS = [
    { value: 'upi', label: 'UPI', icon: Smartphone, hint: 'Scan QR or open in UPI app' },
    { value: 'cash', label: 'Cash', icon: Banknote, hint: 'Paid in cash' },
    { value: 'bank', label: 'Bank transfer', icon: Landmark, hint: 'NEFT / IMPS / RTGS' },
    { value: 'cheque', label: 'Cheque', icon: ScrollText, hint: 'Cheque number in reference' },
];

/**
 * RecordPaymentDialog
 * Records a payment to a vendor with optional UPI intent link/QR.
 *
 * Props:
 *  - open, onOpenChange
 *  - vendor { vendor, vendor_key }
 *  - outstanding (optional, number) — for a helpful "Pay full outstanding" shortcut
 *  - meta { upi_id, upi_name, phone } (optional) — vendor UPI details
 *  - onSuccess(payment) — called after successful save
 *  - onMetaChange(nextMeta) — optional callback if inline UPI-id edits happen
 */
export function RecordPaymentDialog({
    open,
    onOpenChange,
    vendor,
    outstanding,
    meta: metaProp,
    onSuccess,
    onMetaChange,
}) {
    const [amount, setAmount] = useState('');
    const [method, setMethod] = useState('upi');
    const [reference, setReference] = useState('');
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [copied, setCopied] = useState(false);

    // Vendor UPI details are editable inline (persisted via updateVendorMeta on save)
    const [upiId, setUpiId] = useState(metaProp?.upi_id || '');
    const [upiName, setUpiName] = useState(metaProp?.upi_name || vendor?.vendor || '');
    const [savingMeta, setSavingMeta] = useState(false);

    useEffect(() => {
        if (open) {
            setAmount('');
            setReference('');
            setNote('');
            setMethod('upi');
            setUpiId(metaProp?.upi_id || '');
            setUpiName(metaProp?.upi_name || vendor?.vendor || '');
            setCopied(false);
        }
    }, [open, metaProp, vendor]);

    const amountNum = Number(amount);
    const amountValid = amount !== '' && !Number.isNaN(amountNum) && amountNum > 0;

    const upiUrl = useMemo(() => {
        if (method !== 'upi') return null;
        if (!upiId || !amountValid) return null;
        return buildUpiIntentUrl({
            pa: upiId.trim(),
            pn: (upiName || vendor?.vendor || '').trim(),
            am: amountNum,
            tn: `Payment to ${vendor?.vendor || 'vendor'}`,
        });
    }, [method, upiId, upiName, vendor, amountNum, amountValid]);

    async function saveUpiMetaIfChanged() {
        if (!vendor?.vendor_key) return;
        const changed =
            (upiId || '') !== (metaProp?.upi_id || '') ||
            (upiName || '') !== (metaProp?.upi_name || '');
        if (!changed) return;
        setSavingMeta(true);
        try {
            const next = await api.updateVendorMeta(vendor.vendor_key, {
                upi_id: upiId || null,
                upi_name: upiName || null,
            });
            onMetaChange?.(next);
        } catch (_e) {
            /* non-fatal */
        } finally {
            setSavingMeta(false);
        }
    }

    async function handleSubmit() {
        if (!amountValid) {
            toast.error('Please enter a valid amount');
            return;
        }
        setSubmitting(true);
        try {
            // Persist any UPI meta changes silently first
            await saveUpiMetaIfChanged();

            const payment = await api.createVendorPayment(vendor.vendor_key, {
                amount: amountNum,
                method,
                reference: reference.trim() || null,
                note: note.trim() || null,
            });
            toast.success(
                `Recorded ${fmtMoney(amountNum)} to ${vendor?.vendor || 'vendor'}`,
            );
            onSuccess?.(payment);
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to record payment');
        } finally {
            setSubmitting(false);
        }
    }

    async function copyUpiLink() {
        if (!upiUrl) return;
        try {
            await navigator.clipboard.writeText(upiUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            /* ignore */
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="record-payment-dialog">
                <DialogHeader>
                    <DialogTitle>Record payment</DialogTitle>
                    <DialogDescription>
                        Log a payment to{' '}
                        <span className="font-medium text-foreground">{vendor?.vendor}</span>
                        {outstanding !== undefined && outstanding > 0 && (
                            <>
                                {' · '}outstanding{' '}
                                <span className="font-mono">{fmtMoney(outstanding)}</span>
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="space-y-1.5">
                        <div className="flex items-center justify-between">
                            <Label htmlFor="pay-amount">Amount (₹)</Label>
                            {outstanding !== undefined && outstanding > 0 && (
                                <button
                                    type="button"
                                    className="text-[11px] text-primary hover:underline"
                                    onClick={() => setAmount(String(outstanding))}
                                    data-testid="pay-set-outstanding-btn"
                                >
                                    Set to outstanding
                                </button>
                            )}
                        </div>
                        <Input
                            id="pay-amount"
                            type="number"
                            step="0.01"
                            min="0"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            autoFocus
                            data-testid="pay-amount-input"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <Label>Method</Label>
                        <RadioGroup
                            value={method}
                            onValueChange={setMethod}
                            className="grid grid-cols-2 gap-2"
                        >
                            {METHODS.map((m) => {
                                const Icon = m.icon;
                                const active = method === m.value;
                                return (
                                    <label
                                        key={m.value}
                                        htmlFor={`pay-method-${m.value}`}
                                        className={`cursor-pointer flex items-start gap-2 rounded-md border p-3 text-sm transition-colors ${active ? 'border-primary bg-accent' : 'border-border hover:bg-muted/40'}`}
                                    >
                                        <RadioGroupItem
                                            value={m.value}
                                            id={`pay-method-${m.value}`}
                                            data-testid={`pay-method-${m.value}`}
                                        />
                                        <div>
                                            <div className="font-medium flex items-center gap-1.5">
                                                <Icon size={13} /> {m.label}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground">
                                                {m.hint}
                                            </div>
                                        </div>
                                    </label>
                                );
                            })}
                        </RadioGroup>
                    </div>

                    {method === 'upi' && (
                        <div className="space-y-3 rounded-md border border-border p-3 bg-muted/30">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div className="space-y-1">
                                    <Label htmlFor="pay-upi-id" className="text-xs">
                                        Vendor UPI ID
                                    </Label>
                                    <Input
                                        id="pay-upi-id"
                                        placeholder="vendor@upi"
                                        value={upiId}
                                        onChange={(e) => setUpiId(e.target.value)}
                                        data-testid="pay-upi-id-input"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label htmlFor="pay-upi-name" className="text-xs">
                                        Payee name
                                    </Label>
                                    <Input
                                        id="pay-upi-name"
                                        placeholder={vendor?.vendor || 'Payee'}
                                        value={upiName}
                                        onChange={(e) => setUpiName(e.target.value)}
                                        data-testid="pay-upi-name-input"
                                    />
                                </div>
                            </div>
                            {upiUrl ? (
                                <div className="flex flex-col sm:flex-row items-center gap-3 pt-1">
                                    <div className="rounded-md bg-white p-2 border border-border shrink-0">
                                        <QRCodeSVG
                                            value={upiUrl}
                                            size={128}
                                            level="M"
                                            data-testid="pay-upi-qr"
                                        />
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-2 w-full">
                                        <div className="text-xs text-muted-foreground">
                                            Scan the QR with any UPI app (GPay, PhonePe, Paytm, BHIM)
                                            or open the link on your phone.
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            <a
                                                href={upiUrl}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="inline-flex items-center gap-1 rounded-md border border-border bg-background hover:bg-accent px-2.5 py-1.5 text-xs"
                                                data-testid="pay-upi-open-link"
                                            >
                                                <ExternalLink size={12} /> Open UPI app
                                            </a>
                                            <button
                                                type="button"
                                                onClick={copyUpiLink}
                                                className="inline-flex items-center gap-1 rounded-md border border-border bg-background hover:bg-accent px-2.5 py-1.5 text-xs"
                                                data-testid="pay-upi-copy-link"
                                            >
                                                {copied ? <Check size={12} /> : <Copy size={12} />}
                                                {copied ? 'Copied' : 'Copy link'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-xs text-muted-foreground">
                                    Enter the vendor's UPI ID and an amount to generate a QR code.
                                </div>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="pay-ref">Reference (optional)</Label>
                            <Input
                                id="pay-ref"
                                placeholder={
                                    method === 'upi'
                                        ? 'UPI txn ID'
                                        : method === 'bank'
                                            ? 'UTR / txn ref'
                                            : method === 'cheque'
                                                ? 'Cheque number'
                                                : 'Slip / voucher'
                                }
                                value={reference}
                                onChange={(e) => setReference(e.target.value)}
                                data-testid="pay-reference-input"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="pay-note">Note (optional)</Label>
                            <Input
                                id="pay-note"
                                placeholder="e.g. Advance against Jan invoices"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                data-testid="pay-note-input"
                            />
                        </div>
                    </div>
                </div>

                <DialogFooter className="gap-2">
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting || savingMeta}
                        data-testid="pay-cancel-btn"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        disabled={!amountValid || submitting}
                        data-testid="pay-submit-btn"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={14} className="mr-1 animate-spin" />
                                Saving…
                            </>
                        ) : (
                            <>Record {amountValid ? fmtMoney(amountNum) : 'payment'}</>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
